import * as Sentry from "@sentry/react";
import {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import { ZodError } from "zod";

/* Taken from Floodsmart. Not sure if these are useful in general,
   they can be improved later. */
export const DEFAULT_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000 /* Refresh every 5 minutes */,
  retry: false as const,
  refetchOnMount: false as const,
  refetchOnWindowFocus: false as const,
  refetchOnReconnect: true as const,
  refetchIntervalInBackground: true as const,
  refetchInterval: false as const,
};

export type UseQueryOptionsType<T> = Omit<
  UseQueryOptions<T, FetchError, T, QueryKey>,
  "queryKey" | "queryFn"
>;

/* Function to create typed useQuery calls with a parser. */
export function useParsedQuery<T, TError extends Error = ZodError>(
  queryKey: QueryKey,
  fetchFunction: () => Promise<unknown>,
  parseFunction: (json: unknown) => T,
  options?: UseQueryOptionsType<T>
) {
  return useQuery<T, FetchError>({
    queryKey,
    queryFn: async (): Promise<T> => {
      let json: unknown;
      try {
        json = await fetchFunction();
      } catch (error) {
        throw new FetchError(null, "Fetch got error: " + error);
      }

      try {
        const parsed = parseFunction(json);
        return parsed;
      } catch (parseError) {
        Sentry.captureException(parseError, (scope) => {
          scope.setContext("json", { json });
          return scope;
        });
        throw new FetchError(null, (parseError as TError).message);
      }
    },
    ...DEFAULT_QUERY_OPTIONS,
    ...(options ?? {}),
  });
}

/* The same as useParsedQuery, but for useQueries */
export function useParsedQueries<T, TError extends Error = ZodError>(
  queries: {
    queryKey: QueryKey;
    fetchFunction: () => Promise<unknown>;
    parseFunction: (json: unknown) => T;
    options?: UseQueryOptionsType<T>;
  }[]
) {
  return useQueries({
    queries: queries.map(({ queryKey, fetchFunction, parseFunction, options }) => {
      return {
        queryKey,
        queryFn: async (): Promise<T> => {
          const json = await fetchFunction();
          try {
            const parsed: T = parseFunction(json);
            return parsed;
          } catch (parseError) {
            Sentry.captureException(parseError, (scope) => {
              scope.setContext("json", { json });
              return scope;
            });
            throw new FetchError(null, (parseError as TError).message);
          }
        },
        ...DEFAULT_QUERY_OPTIONS,
        ...(options ?? {}),
      };
    }),
  }) as UseQueryResult<T, FetchError>[];
}

// Error to throw if status code isn't 2xx
export class FetchError extends Error {
  constructor(public res: Response | null, message?: string) {
    super(message);
  }
}

type FetchOptions = {
  headers?: RequestInit["headers"];
  method?: RequestInit["method"];
  body?: RequestInit["body"];
};

export async function fetchAllResultsWithError<T = any>(path: string, options: FetchOptions = {}) {
  const allResults: any[] = [];

  // Get first result
  const { results, count } = await fetchWithError(path, options);
  allResults.push(...results);

  // Get all the other results
  const pages = Math.ceil((count - results.length) / results.length);
  if (pages) {
    const createPageUrl = (page: number) =>
      path + (path.includes("?") ? "&" : "?" + "page=") + page;

    const otherPromises = [...new Array(pages)].map((_, index) =>
      fetchWithError(createPageUrl(index + 2), options)
    );

    const otherResults = ((await Promise.all(otherPromises)).map(({ results }) => results) as any)
      .flat()
      .flat();
    allResults.push(...otherResults);
  }

  return allResults as T;
}

// Fetch wrapper that throws an error on bad status
// This one also calls .json() as we do that almost always.
export async function fetchWithError<T = any>(path: string, options: FetchOptions = {}) {
  const response = await fetchWithErrorBareResponse(path, options);
  return (await response.json()) as T;
}

// This one does not call .json() for the times we do that.
export async function fetchWithErrorBareResponse(path: string, options: FetchOptions = {}) {
  const defaultHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const actualHeaders: RequestInit["headers"] = { ...defaultHeaders, ...(options.headers ?? {}) };

  if ("Content-Type" in actualHeaders && actualHeaders["Content-Type"] === "multipart/form-data") {
    // This header is set automatically together with the 'boundary' by fetch, leave it out!
    delete actualHeaders["Content-Type"];
  }

  const response = await fetch(path, {
    method: options.method ?? "GET",
    headers: actualHeaders,
    body: options.body,
  });

  if (response.ok) {
    return response;
  } else {
    if (response.status !== 404) {
      // Log error responses, except 404s as those are expected
      Sentry.captureMessage("fetchWithError response not OK", (scope) => {
        scope.setContext("request", {
          path,
          options,
        });
        scope.setContext("response", { status: response.status, body: response.body });
        return scope;
      });
    }
    throw new FetchError(response, `Received status: ${response.status}`);
  }
}
